import { GroupSecurity } from '@quromedical/fhir-common';
import type {
  AdmissionGetResponse,
  ConsumableCreateRequest,
} from '@quromedical/models';
import { first } from '@quromedical/utils';
import { WithGroup } from 'core/auth';
import { MedicationHistory, Section } from 'design-system';
import { useRevalidation } from 'hooks/useRevalidation';
import { consumableFetcher } from 'integration';
import {
  AdmissionApi,
  admissionSWRKey,
  ConsumableApi,
} from 'integration/aggregate';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { ConsumableTable, CreateConsumableCard } from 'screens/consumable';
import { strings } from 'strings';
import useSWR from 'swr';

interface PatientConsumableTabProps {
  patientId: string;
}
const api = new ConsumableApi();
const admissionApi = new AdmissionApi();

const assignableConsumableFetcher = consumableFetcher();

export const PatientConsumableTab: React.FC<PatientConsumableTabProps> = ({
  patientId,
}) => {
  const revalidate = useRevalidation(patientId);

  const onSubmit = useCallback(
    async (consumableUsage: ConsumableCreateRequest) => {
      await api.createConsumable(consumableUsage);
      revalidate();
    },
    [revalidate],
  );

  const key = useMemo(() => {
    return admissionSWRKey;
  }, []);

  const { data } = useSWR(
    key,
    async (): Promise<AdmissionGetResponse | undefined> => {
      const patientAdmissions = await admissionApi.getAdmissions({
        patient: patientId,
        status: 'in-progress',
      });
      const admission = first(patientAdmissions.admissions);

      return admission;
    },
  );

  return (
    <>
      <WithGroup groups={[GroupSecurity.ChargeItemCreate]}>
        <Section unsetZIndex isVisible={data?.id !== undefined}>
          <CreateConsumableCard
            onSubmit={onSubmit}
            consumableFetcher={assignableConsumableFetcher}
            title={strings.LabelUsedConsumables}
            admission={data?.id as string}
            patient={patientId}
          />
        </Section>
      </WithGroup>

      <MedicationHistory
        patientId={patientId}
        HistoryComponent={ConsumableTable}
        groups={[GroupSecurity.ChargeItemRead]}
      />
    </>
  );
};
